import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useNavigate } from 'react-router-dom'
import ComcastBilling, { MONTHLY } from '../../lib/ComcastBilling.js'
import TVaultDonationService, {
  APPROVED,
  DECLINED,
} from '../../lib/TVaultDonationService'
import { checkPassportStatus } from '../../actions/userActions.js'
import googleAnalytics4 from '../../lib/googleAnalytics.js'
import PBSError from '../../../../shared/PBSError'
import { useAppSelector } from '../../hooks/useStore'
import type { ConsentParams } from '../../types/InAppDonationTypes/consentParamsTypes'
import type { TransactionResponse } from '../../types/InAppDonationTypes/tvaultTransactionResponseTypes'
import type { SubscriptionResponse } from '../../types/InAppDonationTypes/tvaultSubscriptionReponseTypes'
import * as UI from '../../lib/ui'
import type {
  DonationProcessArgs,
  ErrorState,
  UseInAppDonationScreenResult,
} from './types'

export default function useInAppDonationScreen(): UseInAppDonationScreenResult {
  const navigate = useNavigate()
  const station = useAppSelector((state) => state.station)

  const comcastBillingRef = useRef<ComcastBilling>(new ComcastBilling())
  const tvaultRef = useRef<TVaultDonationService>(
    new TVaultDonationService(station.call_sign)
  )

  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<ErrorState | null>(null)

  useEffect(() => {
    window.focusedComponent = window.focusedComponent ?? {}
    window.focusedComponent.handleBySpatialNavigation = true
    const tvaultService = tvaultRef.current
    return () => {
      window.focusedComponent.handleBySpatialNavigation = false
      tvaultService.clearTimeout()
    }
  }, [])

  const monthlyAmount = useMemo((): number => {
    const opts = station.donation_campaign?.monthly_amount_options
    if (opts && opts.length > 0) return Number(opts[0].amount)
    return Number(station.donation_campaign?.minimum_monthly_amount || 5)
  }, [station])

  const oneTimeAmount = useMemo((): number => {
    const opts = station.donation_campaign?.onetime_amount_options
    if (opts && opts.length > 0) return Number(opts[0].amount)
    return Number(station.donation_campaign?.minimum_amount || 60)
  }, [station])

  const executeDonationProcess = useCallback(
    async ({
      totalChargeAmount,
      donationFrequency,
      donationType,
    }: DonationProcessArgs): Promise<void> => {
      let oat = 'undefined'
      const passportStatus = await checkPassportStatus(station.call_sign)

      if (passportStatus === 'PENDING') {
        navigate('/passport-activation', {
          state: {
            navigatingFromDonation: true,
            stationTitle: station.short_common_name,
          },
        })
        return
      }

      if (passportStatus === 'ACTIVATED') {
        navigate(-1)
        return
      }

      try {
        setIsLoading(true)

        const { consentToken, oat: at } =
          await comcastBillingRef.current.purchase({
            totalChargeAmount,
            donationType,
            donationFrequency,
          })

        oat = at

        const { id: donationCampaignId } = station.donation_campaign

        tvaultRef.current.setDonationFrequency(donationFrequency)
        tvaultRef.current.setDonationPayload(
          comcastBillingRef.current.consentPayload as ConsentParams,
          {
            donationCampaignId,
            totalChargeAmount,
            consentToken,
            oat,
          }
        )

        const { donationResponse } = (await tvaultRef.current.triggerProcess()) as {
          donationResponse: TransactionResponse | SubscriptionResponse
        }

        const donationStatus =
          tvaultRef.current.getDonationResponseStatus(donationResponse)

        if ([APPROVED, DECLINED].includes(donationStatus)) {
          setIsLoading(false)

          if (donationStatus === APPROVED) {
            googleAnalytics4.sendCustomEvent(
              'donation_success',
              {
                donation_frequency:
                  donationFrequency === MONTHLY ? 'monthly' : 'one-time',
                donation_amount: `$${totalChargeAmount}/${
                  donationFrequency === MONTHLY ? 'month' : 'one-time'
                }`,
                donation_type: donationType,
                external_key: oat,
              },
              'In App Donation Screen'
            )

            tvaultRef.current.configureMvaultId(donationResponse)

            navigate('/passport-activation', {
              state: {
                stationTitle: station.short_common_name,
                navigatingFromDonation: true,
                customDonationType: false,
              },
            })
          }

          if (donationStatus === DECLINED) {
            const err = new Error('Declined donation.')
            const pbsError = new PBSError(err, 'TVAULT_ERROR')
            pbsError.textErrorCode =
              tvaultRef.current.getDonationDeclinedReason(donationResponse)

            PBSError.sendSentryError({
              errorString: 'TVault service error',
              errorTitle: 'InAppDonationScreen',
              extraParams: {
                errorCodeNumber: pbsError.errorStatus,
                errorCodeText: err.name,
                errorMessage: err.message,
              },
            })

            throw pbsError
          }
        }
      } catch (e) {
        if (e instanceof PBSError) {
          googleAnalytics4.sendCustomEvent(
            'donation_error',
            {
              donation_frequency:
                donationFrequency === MONTHLY ? 'monthly' : 'one-time',
              donation_amount: `$${totalChargeAmount}/${
                donationFrequency === MONTHLY ? 'month' : 'one-time'
              }`,
              donation_type: donationType,
              external_key: oat,
              error_code_id: e.errorStatus,
              error_code_title: e.errorCode,
              error_code_text: e.errorDetail,
            },
            'In App Donation Screen'
          )

          const { errorMessage } = e.parse()
          const buttons: ErrorState['buttons'] = []

          if (e.shouldTryAgainButton) {
            buttons.push({
              label: 'Try again',
              focusKey: 'error-try-again',
              onEnterPress: () => {
                setError(null)
                executeDonationProcess({
                  totalChargeAmount,
                  donationFrequency,
                  donationType,
                })
              },
            })
          }
          if (e.goBackButton) {
            buttons.push({
              label: 'Go back',
              focusKey: 'error-go-back',
              onEnterPress: () => setError(null),
            })
          }
          if (e.okButton) {
            buttons.push({
              label: 'OK',
              focusKey: 'error-ok',
              onEnterPress: () => navigate(-1),
            })
          }

          setError({ ...errorMessage, buttons })
        }

        setIsLoading(false)
      }
    },
    [station, navigate]
  )

  const handleGoBack = useCallback(() => {
    navigate(-1)
  }, [navigate])

  const handleWhatIsPassport = useCallback(() => {
    navigate('/what-is-passport')
  }, [navigate])

  return {
    stationName: station.short_common_name,
    preDonationCopy: station.donation_campaign?.pre_donation_copy ?? '',
    monthlyAmount,
    oneTimeAmount,
    error,
    isLoading,
    executeDonationProcess,
    handleGoBack,
    handleWhatIsPassport,
  }
}
